import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "./config";
import { useBranding } from "./BrandingContext";
import { useTheme } from "./ThemeContext";
import {
  FiKey, FiPlus, FiTrash2, FiCopy, FiCheck, FiArrowLeft,
  FiAlertTriangle, FiShield, FiClock, FiActivity,
} from "react-icons/fi";

export default function ApiKeysPage() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { theme } = useTheme();
  const dark = theme === "dark";

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [keyType, setKeyType] = useState("live");
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [error, setError] = useState("");

  const accentColor = branding?.accentColor || "#f59e0b";

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${API_BASE_URL}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKeys(res.data.api_keys || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load API keys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async () => {
    setCreating(true);
    setNewKeyValue(null);
    setError("");
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(
        `${API_BASE_URL}/api-keys`,
        { type: keyType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewKeyValue(res.data.api_key.key);
      fetchKeys();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create API key.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(`${API_BASE_URL}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRevokeConfirm(null);
      fetchKeys();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to revoke API key.");
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const formatDate = (iso) => {
    if (!iso) return "Never";
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const bg = dark ? "#0f172a" : "#f8fafc";
  const card = dark ? "#1e293b" : "#ffffff";
  const border = dark ? "#334155" : "#e2e8f0";
  const text = dark ? "#f1f5f9" : "#1e293b";
  const muted = dark ? "#94a3b8" : "#64748b";

  return (
    <div style={{ minHeight: "100vh", background: bg, color: text, fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ background: card, borderBottom: `1px solid ${border}`, padding: "16px 32px", display: "flex", alignItems: "center", gap: "16px" }}>
        <button
          onClick={() => navigate("/dashboard")}
          style={{ background: "none", border: "none", color: muted, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "14px" }}
        >
          <FiArrowLeft size={16} /> Back to Dashboard
        </button>
        <div style={{ width: "1px", height: "20px", background: border }} />
        <FiKey size={20} style={{ color: accentColor }} />
        <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>API Keys</h1>
      </div>

      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "40px 24px" }}>

        {/* Info Banner */}
        <div style={{ background: dark ? "#1a2744" : "#eff6ff", border: `1px solid ${dark ? "#2d4070" : "#bfdbfe"}`, borderRadius: "12px", padding: "16px 20px", marginBottom: "32px", display: "flex", gap: "12px" }}>
          <FiShield size={18} style={{ color: "#60a5fa", flexShrink: 0, marginTop: "1px" }} />
          <p style={{ margin: 0, fontSize: "14px", color: dark ? "#93c5fd" : "#1d4ed8", lineHeight: "1.5" }}>
            API keys grant programmatic access to your account. Keep them secret — treat them like passwords.
            Full key values are only shown once, immediately after creation.
          </p>
        </div>

        {error && (
          <div style={{ background: dark ? "#2d1515" : "#fef2f2", border: `1px solid ${dark ? "#7f1d1d" : "#fecaca"}`, borderRadius: "10px", padding: "12px 16px", marginBottom: "24px", display: "flex", gap: "10px", alignItems: "center" }}>
            <FiAlertTriangle size={16} style={{ color: "#ef4444" }} />
            <span style={{ fontSize: "14px", color: "#ef4444" }}>{error}</span>
          </div>
        )}

        {/* New Key Reveal */}
        {newKeyValue && (
          <div style={{ background: dark ? "#14261e" : "#f0fdf4", border: `1px solid ${dark ? "#166534" : "#bbf7d0"}`, borderRadius: "12px", padding: "20px", marginBottom: "28px" }}>
            <p style={{ margin: "0 0 8px", fontSize: "13px", fontWeight: 600, color: "#22c55e" }}>
              New API Key Created — Copy it now, it won't be shown again
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", background: dark ? "#0f1f18" : "#dcfce7", borderRadius: "8px", padding: "10px 14px" }}>
              <code style={{ flex: 1, fontSize: "13px", wordBreak: "break-all", color: dark ? "#86efac" : "#15803d" }}>{newKeyValue}</code>
              <button
                onClick={() => handleCopy(newKeyValue, "new")}
                style={{ background: "none", border: "none", cursor: "pointer", color: copiedId === "new" ? "#22c55e" : muted, padding: "4px" }}
              >
                {copiedId === "new" ? <FiCheck size={16} /> : <FiCopy size={16} />}
              </button>
            </div>
          </div>
        )}

        {/* Create New Key */}
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: "16px", padding: "28px", marginBottom: "32px" }}>
          <h2 style={{ margin: "0 0 20px", fontSize: "17px", fontWeight: 600 }}>Generate New API Key</h2>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: "8px" }}>
              {["live", "test"].map((type) => (
                <button
                  key={type}
                  onClick={() => setKeyType(type)}
                  style={{
                    padding: "8px 18px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500,
                    border: keyType === type ? `2px solid ${accentColor}` : `1px solid ${border}`,
                    background: keyType === type ? (dark ? "#1e293b" : "#fffbeb") : "transparent",
                    color: keyType === type ? accentColor : muted,
                  }}
                >
                  {type === "live" ? "Live Key" : "Test Key"}
                </button>
              ))}
            </div>
            <div style={{ fontSize: "13px", color: muted, flex: 1 }}>
              {keyType === "live" ? "sk_live_... — for production use" : "sk_test_... — for development/testing"}
            </div>
            <button
              onClick={handleCreate}
              disabled={creating}
              style={{
                padding: "10px 20px", borderRadius: "10px", cursor: creating ? "not-allowed" : "pointer",
                background: `linear-gradient(135deg, ${accentColor}, #d97706)`, color: "#000",
                border: "none", fontWeight: 600, fontSize: "14px", opacity: creating ? 0.7 : 1,
                display: "flex", alignItems: "center", gap: "8px",
              }}
            >
              <FiPlus size={16} />
              {creating ? "Generating..." : "Generate Key"}
            </button>
          </div>
        </div>

        {/* Keys List */}
        <div style={{ background: card, border: `1px solid ${border}`, borderRadius: "16px", overflow: "hidden" }}>
          <div style={{ padding: "20px 28px", borderBottom: `1px solid ${border}`, display: "flex", alignItems: "center", gap: "10px" }}>
            <FiActivity size={16} style={{ color: muted }} />
            <h2 style={{ margin: 0, fontSize: "17px", fontWeight: 600 }}>Your API Keys</h2>
            <span style={{ marginLeft: "auto", fontSize: "13px", color: muted }}>{keys.length} key{keys.length !== 1 ? "s" : ""}</span>
          </div>

          {loading ? (
            <div style={{ padding: "48px", textAlign: "center", color: muted }}>Loading keys...</div>
          ) : keys.length === 0 ? (
            <div style={{ padding: "64px 32px", textAlign: "center" }}>
              <FiKey size={40} style={{ color: muted, marginBottom: "16px" }} />
              <p style={{ color: muted, margin: 0 }}>No API keys yet. Generate your first key above.</p>
            </div>
          ) : (
            <div>
              {keys.map((key, idx) => (
                <div
                  key={key.id}
                  style={{
                    padding: "18px 28px",
                    borderBottom: idx < keys.length - 1 ? `1px solid ${border}` : "none",
                    display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap",
                  }}
                >
                  <div style={{
                    padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
                    background: key.type === "live" ? (dark ? "#14261e" : "#f0fdf4") : (dark ? "#1a2744" : "#eff6ff"),
                    color: key.type === "live" ? "#22c55e" : "#60a5fa",
                  }}>
                    {key.type === "live" ? "LIVE" : "TEST"}
                  </div>

                  <code style={{ flex: 1, fontSize: "13px", color: muted, letterSpacing: "0.05em" }}>
                    {key.key_display}
                  </code>

                  <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: muted }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <FiClock size={12} /> Created {formatDate(key.createdAt)}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <FiActivity size={12} /> Last used {formatDate(key.lastUsedAt)}
                    </span>
                  </div>

                  {revokeConfirm === key.id ? (
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <span style={{ fontSize: "13px", color: "#ef4444" }}>Revoke?</span>
                      <button
                        onClick={() => handleRevoke(key.id)}
                        style={{ padding: "5px 12px", background: "#ef4444", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setRevokeConfirm(null)}
                        style={{ padding: "5px 12px", background: "transparent", color: muted, border: `1px solid ${border}`, borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setRevokeConfirm(key.id)}
                      title="Revoke key"
                      style={{ background: "none", border: "none", cursor: "pointer", color: muted, padding: "6px", borderRadius: "6px" }}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Security Note */}
        <div style={{ marginTop: "24px", padding: "16px 20px", background: dark ? "#1e1a0f" : "#fffbeb", border: `1px solid ${dark ? "#78350f" : "#fde68a"}`, borderRadius: "12px", fontSize: "13px", color: dark ? "#fcd34d" : "#92400e" }}>
          <strong>Security tip:</strong> Rotate your API keys regularly and never expose them in client-side code or public repositories.
        </div>
      </div>
    </div>
  );
}
